import {useEffect, useState} from 'react';
import {Paginator} from 'src/design/organisms/Pagination';

import {Box} from 'src/design/atoms/Box';
import {Space} from 'src/design/atoms/Space';
import type {SourceEntityRecord} from 'src/api/ProactApiClient';
import {EntityTypeEnum} from 'src/api/ProactApiClient';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetVendorListQuery} from 'src/redux/queries/vendor';

export interface VendorListProps {
  selected: SourceEntityRecord[];
  setSelected: React.Dispatch<React.SetStateAction<SourceEntityRecord[]>>;
  showOnlySelected: boolean;
  showSelectedAll: boolean;
  search?: string;
  itemsPerPage?: number;
  /** Callback for unfiltered total count (used for percentage calculation) */
  onTotalCountChange?: (count: number) => void;
}

export const VendorList: React.FC<VendorListProps> = ({
  selected,
  setSelected,
  showOnlySelected,
  showSelectedAll,
  search,
  itemsPerPage = 25,
  onTotalCountChange,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Baseline query for unfiltered total count (used for percentage calculation)
  const {data: baselineData} = useGetVendorListQuery({
    active: true,
    start: 0,
    length: 0,
  });

  // Filtered query for display and pagination
  const {
    data: vendors,
    isFetching: isVendorsFetching,
    isLoading: isVendorsLoading,
  } = useGetVendorListQuery({
    search: search,
    active: true,
    start: currentPage * itemsPerPage - itemsPerPage,
    length: itemsPerPage,
  });

  const isLoading = isVendorsFetching || isVendorsLoading;

  // Report unfiltered total count for percentage calculation
  useEffect(() => {
    if (onTotalCountChange && baselineData?.totalRecords !== undefined) {
      onTotalCountChange(baselineData.totalRecords);
    }
  }, [baselineData?.totalRecords, onTotalCountChange]);

  // The vendor list endpoint has no server-side whitelistIds filter, so
  // "Show Only Selected" is applied client-side against the current page.
  const selectedIds = new Set(selected.map(i => Number(i.entityId)));
  const visibleVendors = showOnlySelected
    ? vendors?.data?.filter(item => selectedIds.has(item.id))
    : vendors?.data;

  function isSelected(id: number): boolean {
    return (
      selected.filter(element => element.entityId.toString() === id.toString())
        .length > 0 || showSelectedAll
    );
  }

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const {checked} = e.target;
    if (checked) {
      setSelected([
        ...selected,
        {
          entityId: id,
          entityType: EntityTypeEnum.Vendor,
        },
      ]);
    } else {
      setSelected(selected.filter(i => i.entityId !== id));
    }
  };

  const handlePageChange = (_: unknown, value: number) => {
    setCurrentPage(value);
  };

  return (
    <Box>
      <Paginator
        mt={8}
        mb={16}
        currentPage={currentPage}
        totalRecords={vendors?.totalRecords ?? 0}
        perPage={itemsPerPage}
        showRecordCount={false}
        onChange={handlePageChange}
      />
      {!isLoading ? (
        visibleVendors?.length ? (
          visibleVendors.map(item => (
            <CheckboxRow
              key={item.id}
              id={String(item.id)}
              label={item.name ?? `#${item.id}`}
              checked={isSelected(item.id)}
              onChange={e => handleSelect(e, Number(item.id))}
              disabled={showSelectedAll}
            />
          ))
        ) : (
          <Box mt="lg">No selections found</Box>
        )
      ) : (
        <>
          <TableBodyRender isLoading={isLoading} />
          <Space mb="xl" />
        </>
      )}
    </Box>
  );
};
